/**
 * API shapes. Mirrors the backend's `src/types.ts`; kept free of web-only
 * imports so the React Native client can consume this file unchanged.
 */

export type Country = 'IN' | 'US' | 'CA';
export type Channel = 'whatsapp' | 'email' | 'sms';

/** GET /auth/otp/delivery/:deliveryId */
export interface OtpDeliveryStatus {
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  reason?: string;
}

/** Who an invoice is made out to; asked at checkout. The state is `Account.billingState`. */
export interface BillingDetails {
  name: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postalCode: string;
}

export interface Account {
  accountId: string;
  name: string;
  country: Country;
  locale: 'en' | 'hi';
  phone?: string;
  phoneVerified: boolean;
  email?: string;
  emailVerified: boolean;
  hasPassword: boolean;
  marketingOptIn: boolean;
  /** GST state code (e.g. '08' Rajasthan): the place of supply on Indian invoices. */
  billingState?: string;
  /** Billing name, phone and address for invoices. */
  billing?: BillingDetails;
  /** Daily WhatsApp nudge with today's programme pick. */
  reminders?: ReminderSettings;
  /** Where reports and receipts go; absent until chosen at checkout or in Settings. */
  deliveryPreferences?: { email: boolean; whatsapp: boolean };
  /** The parent replied STOP on WhatsApp; only sign-in codes go there until they turn it back on. */
  whatsappOptOut?: boolean;
  role: 'parent' | 'admin';
  /** A check-in started without an account: may add a child and answer, nothing else, until sign-up. */
  guest?: boolean;
  createdAt: string;
}

export const REMINDER_HOURS = [7, 9, 12, 16, 18, 20] as const;
export interface ReminderSettings {
  whatsapp: boolean;
  hour: number;
}

export interface Child {
  childId: string;
  /** First name — what the app, reports and PDFs call the child. */
  name: string;
  lastName?: string;
  /** `YYYY-MM-DD`. Children added before it was asked have none. */
  dob?: string;
  ageYears: number;
  ageMonths: number;
  gender: 'male' | 'female' | 'other' | 'undisclosed';
  context?: { existingSupport?: string; primaryLanguage?: string; schoolContext?: string };
  /** "About my child": personalises the app; never used by reports or PDFs. */
  about?: ChildAbout;
  createdAt: string;
}

/** What the add-child form sends: the age is worked out server-side from the date of birth. */
export interface ChildCreateInput {
  name: string;
  lastName?: string;
  dob: string;
  gender: Child['gender'];
  about?: ChildAboutInput;
}

// ── Profile features: about my child, privacy centre, care team, family ─────

export type DiagnosisStatus = 'none' | 'waiting' | 'diagnosed' | 'unsure';
export type ConditionKey =
  | 'autism' | 'adhd' | 'learning' | 'speech' | 'developmental_delay' | 'intellectual_disability'
  | 'sensory' | 'coordination' | 'anxiety' | 'down_syndrome' | 'cerebral_palsy' | 'other';
export type TherapyKey = 'speech' | 'occupational' | 'behavioural' | 'special_education' | 'physiotherapy' | 'psychology' | 'other';
export type SchoolSetting = 'not_yet' | 'mainstream' | 'inclusive' | 'special' | 'home';

export interface ChildAboutInput {
  diagnosis?: { status: DiagnosisStatus; conditions: ConditionKey[]; other?: string };
  therapies?: TherapyKey[];
  school?: { setting?: SchoolSetting; grade?: string };
  languages?: string[];
  loves?: string;
  upsets?: string;
}
export interface ChildAbout extends ChildAboutInput {
  updatedAt: string;
}

export interface ConsentEvent {
  kind: 'terms' | 'marketing' | 'reminders' | 'nominee';
  action: 'accepted' | 'on' | 'off' | 'set' | 'removed';
  version?: string;
  at: string;
}
export type NomineeRelation = 'spouse' | 'parent' | 'sibling' | 'grandparent' | 'relative' | 'friend' | 'other';
export interface Nominee {
  name: string;
  relation: NomineeRelation;
  phone?: string;
  email?: string;
  addedAt: string;
  updatedAt: string;
}
export type DataRequestKind = 'export' | 'delete_data' | 'delete_account' | 'correction' | 'withdraw_consent' | 'nominee_request' | 'other';
export interface DataRequest {
  requestId: string;
  kind: DataRequestKind;
  status: 'completed' | 'received' | 'resolved';
  channel: 'self_service' | 'in_app';
  details?: string;
  createdAt: string;
  resolvedAt?: string;
  resolutionNote?: string;
}
export interface PrivacyCentre {
  consentHistory: ConsentEvent[];
  marketingOptIn: boolean;
  nominee?: Nominee;
  requests: DataRequest[];
  grievanceEmail: string;
}

export type CareRole =
  | 'paediatrician' | 'developmental_paediatrician' | 'psychologist' | 'psychiatrist' | 'speech_therapist'
  | 'occupational_therapist' | 'special_educator' | 'teacher' | 'other';
export interface CareMember {
  memberId: string;
  childId: string;
  name: string;
  role: CareRole;
  organisation?: string;
  phone?: string;
  email?: string;
  createdAt: string;
}
export type CareMemberInput = Pick<CareMember, 'name' | 'role' | 'organisation' | 'phone' | 'email'>;
export interface ReportShare {
  shareId: string;
  reportId: string;
  memberId?: string;
  memberName?: string;
  tier: 'full' | 'preview';
  expiresAt: string;
  state: 'active' | 'expired' | 'revoked';
  opens: number;
  lastOpenedAt?: string;
  createdAt: string;
}
export interface SharedReportView {
  childName: string;
  sharedBy?: string;
  sharedWith?: string;
  reportRef: string;
  preparedAt: string;
  expiresAt: string;
  tier: 'full' | 'preview';
  disclaimer: string;
}

export type FamilyRelation = 'co_parent' | 'grandparent' | 'relative' | 'caregiver' | 'other';
export interface FamilyInvite {
  inviteId: string;
  name: string;
  relation: FamilyRelation;
  channel: 'whatsapp' | 'email';
  identifier: string;
  expiresAt: string;
  state: 'pending' | 'accepted' | 'expired';
  createdAt: string;
}
export interface FamilyMember {
  memberAccountId: string;
  name: string;
  relation: FamilyRelation;
  addedAt: string;
}
export interface FamilyAccess {
  ownerAccountId: string;
  ownerName: string;
  relation: FamilyRelation;
  addedAt: string;
}
export interface FamilyOverview {
  members: FamilyMember[];
  invites: FamilyInvite[];
  access: FamilyAccess[];
}
export interface JoinPreview {
  ownerName?: string;
  name: string;
  relation: FamilyRelation;
  channel: 'whatsapp' | 'email';
  identifier: string;
  expiresAt: string;
  state: 'pending' | 'accepted' | 'expired';
}

export interface QuestionOption {
  value: string;
  label: string;
}

// ── Observa engine: parent-facing assessment flow ──────────────────────────

export type AreaKey = 'milestones' | 'adaptive' | 'connecting' | 'attention' | 'teen' | 'sensory';
export type ResultBand = 'ok' | 'watch' | 'discuss' | 'na';
export type Stage = 'quick' | 'closer';
export type AnswerValue = number | string | string[] | null;

export type ClientEntry =
  // `domain` groups tick-list rows under a subheading. Optional on purpose: engine rules v3 sends
  // it, earlier engines do not, and the two repositories deploy through separate pipelines — so a
  // web build that reaches a parent ahead of the backend degrades to an ungrouped list rather than
  // rendering "undefined".
  | {
      kind: 'item';
      id: string;
      stage: Stage;
      area: AreaKey;
      domain?: string;
      /** The item's age level. A tick-list starts a new page at each edge. Optional like `domain`. */
      band?: string;
      /** Parent-friendly heading a tick-list groups its rows under; falls back to `domain`. */
      topic?: string;
      text: string;
      options: readonly string[];
      swipeable: boolean;
      /** The "i" text: what the question means and one example. Optional for the same reason as `domain`. */
      help?: { means: string; example?: string };
    }
  | { kind: 'checkpoint'; flagged: Array<{ area: AreaKey; label: string; closerCount: number; minutes: number }> }
  /** `options` are the values sent back; `labels` (Hindi responses) are what the parent reads. */
  | { kind: 'strengths'; options: readonly string[]; labels?: readonly string[] }
  | { kind: 'note' };

// ── Engine rules v2: pre-questionnaire intake ───────────────────────────────

export type PriorTesting = 'yes' | 'no' | 'unsure';
export type FormalLevel = 'none' | 'mild' | 'moderate' | 'severe';
export interface FormalScores {
  quotient?: { type: 'iq' | 'dq' | 'sq'; value: number };
  autism?: { level: FormalLevel };
  adhd?: { level: FormalLevel };
  /** Month of the professional assessment (`YYYY-MM`), when the parent knows it. */
  testedOn?: string;
  confirmed: boolean;
}

/** Route 3 — "just describe my concerns": a guidance note, not a report. */
export interface GuidanceIntake {
  concerns: string[];
  goal?: string;
  acknowledged: boolean;
}

export type IntakeBody =
  | { mode?: 'assessment'; priorTesting: PriorTesting; declined?: boolean; formal?: FormalScores }
  | { mode: 'guidance'; guidance: GuidanceIntake };
export type Requirement = 'quotient' | 'autism' | 'adhd';
export type IntakeNote =
  | { code: 'age_range' }
  | { code: 'unsure' }
  | { code: 'declined' }
  | { code: 'over_15'; ageMonths: number }
  | { code: 'report_only'; sensory: boolean }
  | { code: 'missing_tests'; missing: Requirement[] }
  | { code: 'autism_too_young' }
  | { code: 'no_attention' }
  | { code: 'beyond_teen_norms' }
  | { code: 'sensory' }
  | { code: 'stale_results'; monthsOld: number }
  | { code: 'guidance_only' };
export interface IntakeView {
  /** `false` for older assessments started before engine rules v2 — skip the intake UI entirely. */
  required: boolean;
  nextStep: 'prior_testing' | 'formal_scores' | 'questionnaire' | 'concerns' | 'guidance';
  path: 'A' | 'B' | 'C' | 'D' | 'G' | null;
  mode?: 'assessment' | 'guidance';
  guidance?: GuidanceIntake;
  priorTesting?: PriorTesting;
  declined?: boolean;
  formal?: FormalScores;
  notes: IntakeNote[];
  coveredByReports: Requirement[];
}
export interface FlowAlert {
  kind: 'safety';
  area: 'teen';
  helpline: { name: string; number: string };
}

export interface ClientFlow {
  engineVersion: string;
  areas: Array<{ key: AreaKey; label: string; covers: string; quickCount: number; closerCount: number }>;
  queue: ClientEntry[];
  answered: Record<string, number | string | string[]>;
  progress: { answered: number; total: number };
  missing: string[];
  intake: IntakeView;
  alerts: FlowAlert[];
}

export interface Assessment {
  assessmentId: string;
  accountId: string;
  childId: string;
  questionBankVersion: string;
  locale: 'en' | 'hi';
  ageMonths: number;
  region: 'india' | 'usa';
  status: 'draft' | 'submitted';
  startedAt: string;
  submittedAt?: string;
  updatedAt: string;
}
export interface AssessmentResponse {
  assessment: Assessment;
  child: Child;
  flow: ClientFlow;
}
export interface SaveAnswersResponse {
  savedAt: string;
  flow: ClientFlow;
}
export interface SetIntakeResponse {
  assessment: Assessment;
  flow: ClientFlow;
}
export interface SubmitResponse {
  reportIds: string[];
  reportId: string;
  refs: string[];
  status: 'generating';
}

// ── Report document: six typed pages ────────────────────────────────────────

export type Tier = 'preview' | 'full';
/** 1-based. Area reports have six pages; a home programme has as many as it needs. */
export type PageNumber = number;
export type Pillar = 'OT' | 'SI' | 'ABA';
export type Scene = 'motor' | 'sensory' | 'social' | 'communication' | 'play';
export type Block =
  | { kind: 'paragraph'; text: string }
  | { kind: 'heading'; text: string }
  | { kind: 'callout'; text: string; tone: 'sage' | 'apricot' | 'provider' }
  | { kind: 'band'; label: string; band: ResultBand; bandLabel: string; line: string }
  | { kind: 'chips'; items: string[] }
  | { kind: 'rows'; items: Array<{ label: string; value: string }> }
  | {
      kind: 'answers';
      groups: Array<{
        label: string;
        answered: number;
        stoodOut: number;
        items: Array<{ text: string; answer: string; concern: boolean }>;
      }>;
    }
  | { kind: 'activity'; index: number; title: string; steps: string[]; whenHard: string }
  | { kind: 'tracker'; activities: string[] }
  | { kind: 'lines'; labels: string[] }
  | {
      kind: 'score';
      headline: string;
      band: ResultBand;
      bandLabel: string;
      rows: Array<{ label: string; value: string }>;
      meter?: { min: number; max: number; value: number; cuts: [number, number]; reversed?: boolean };
    }
  | { kind: 'steps'; items: string[] }
  | { kind: 'pillarIntro'; pillar: Pillar; label: string; weekGoal: string; focus: string; activities: string[] }
  | {
      kind: 'exercise';
      index: number;
      pillar: Pillar;
      exerciseId: string;
      title: string;
      slot?: string;
      level: number;
      durationMinutes: number;
      why: string;
      materials: string[];
      steps: string[];
      successSignal: string;
      whenHard: string;
      caution?: string;
      scene: Scene;
      /** Where at home the activity happens (added in API v1.2; older payloads may lack it). */
      room?: RoomKey;
      roomLabel?: string;
      /** 2 when the activity joins in week 3 (programmes from 13 October 2026); absent on older ones, where all start on day one. */
      phase?: 1 | 2;
      /** When to move on, in the library's words. */
      moveOnWhen?: string;
    }
  | { kind: 'radar'; axes: Array<{ label: string; value: number }>; reference: number }
  | { kind: 'timeline'; items: Array<{ skill: string; domain: string }> }
  // Programme plan sections (API 2026-09-25). Mirrors the backend's src/pdf/documentTypes.ts.
  | {
      kind: 'cover';
      name: string;
      ageLabel: string;
      week: number;
      prepared: string;
      ref: string;
      reviewed: boolean;
      scene: boolean;
      facts: Array<{ label: string; value: string }>;
      contents?: Array<{ title: string; page: number }>;
    }
  | { kind: 'glance'; areas: Array<{ label: string; band: ResultBand; bandLabel: string; fromReports?: boolean }> }
  | { kind: 'bars'; items: Array<{ label: string; value: number; alert: boolean }>; reference: number; caption: string }
  | { kind: 'goals'; items: Array<{ title: string; area: string; now?: string; target: string; measure: string }> }
  | { kind: 'routine'; rows: Array<{ time: string; what: string; activities: string[]; minutes?: number; note?: string }>; reward: string }
  | { kind: 'fridge'; rows: Array<{ situation: string; say: string; instead: string; then: string }> }
  | { kind: 'table'; columns: string[]; rows: string[][]; tone?: 'plain' | 'alert' }
  | { kind: 'roles'; items: Array<{ role: string; why: string; ask: string; icon?: RoleIcon }> }
  | { kind: 'note'; lines: string[]; signoff: string };
export type RoleIcon = 'doctor' | 'hand' | 'speech' | 'mind' | 'school' | 'people' | 'heart';
export interface OpenPage {
  number: PageNumber;
  title: string;
  subtitle?: string;
  locked: false;
  blocks: Block[];
}
export interface LockedPage {
  number: PageNumber;
  title: string;
  locked: true;
  description: string;
}
export type ReportPage = OpenPage | LockedPage;
export interface ReportDocument {
  version: string;
  ref: string;
  area: { key: AreaKey; label: string; covers: string };
  child: { name: string; ageLabel: string };
  preparedAt: string;
  band: ResultBand;
  bandLabel: string;
  disclaimer: string;
  pages: ReportPage[];
}

/** The session's home programme (all areas together); served tier-filtered like `ReportDocument`. */
export interface ProgrammeDocument {
  version: string;
  ref: string;
  week: number;
  child: { name: string; ageLabel: string };
  preparedAt: string;
  /** Present until the activity wording is clinically approved. */
  watermark?: string;
  disclaimer: string;
  pages: ReportPage[];
}

/**
 * The one behaviour question included with a paid session. `available` covers both "not asked yet"
 * and "the last attempt failed" (`failed`); only `answered` has spent the question. `unavailable`:
 * this deployment has no plan service, so nothing is shown.
 */
export type BehaviourQueryView =
  | { status: 'locked' }
  | { status: 'unavailable' }
  | { status: 'available'; failed: boolean; attemptsLeft: number }
  | { status: 'generating'; query: string }
  | { status: 'answered'; query: string; plan: ProgrammeDocument }
  | { status: 'escalated'; query: string; message: string };

/** Anything the document viewer can render: pages are all it needs. */
export type ViewableDocument = Pick<ReportDocument, 'pages'>;

// ── Reports ──────────────────────────────────────────────────────────────

export type ReportStatus =
  | 'generating'
  | 'preview_available'
  | 'payment_pending'
  | 'unlocked'
  | 'failed';

export interface AssessmentPreview {
  key: AreaKey;
  label: string;
  band: ResultBand;
  bandLabel: string;
  line: string;
  stoodOut: Array<{ text: string; answer: string }>;
  /** Set when this area's result was filled from a professional's written report rather than the questionnaire. */
  source?: 'formal';
  /** Set when this area raised a safety concern (see `ClientFlow.alerts`). */
  safety?: true;
}

export interface ReportContent {
  tier: Tier;
  assessment?: AssessmentPreview;
  document?: ReportDocument;
  /** The session home programme; cover + snapshot open before unlock, everything after. */
  programme?: ProgrammeDocument;
  /** Route 3 only. Until unlocked: summary + concern titles (`locked: true`). */
  guidance?: GuidanceNoteView;
  disclaimer: string;
}

export interface GuidanceNoteView {
  status: 'ok' | 'safety' | 'out_of_scope';
  summary: string;
  sections: Array<{ concern: string; what_it_can_mean?: string; try_at_home?: string[]; when_to_seek_help?: string }>;
  next_steps: string[];
  handoff: string;
  locked: boolean;
  helpline: { name: string; number: string };
}

export type ReportKind = 'area' | 'guidance';

export interface ReportSummary {
  reportId: string;
  ref: string;
  childId: string;
  childName: string;
  status: ReportStatus;
  version: number;
  createdAt: string;
  updatedAt: string;
  assessmentId: string;
  assessmentKey?: AreaKey;
  kind?: ReportKind;
  area?: string;
  band?: ResultBand;
  bandLabel?: string;
  /** Set when this area's result was filled from a professional's written report rather than the questionnaire. */
  source?: 'formal';
  /** Set when this area raised a safety concern (see `ClientFlow.alerts`). */
  safety?: true;
  entitlement?: { regenerationsRemaining: number; regenerationsUsed: number; expiresAt: string };
}

export interface ReportDetail {
  report: {
    reportId: string;
    ref: string;
    childId: string;
    childName: string;
    assessmentId: string;
    kind?: ReportKind;
    status: ReportStatus;
    version: number;
    failureReason?: string;
    createdAt: string;
    unlockedAt?: string;
    deliveryPreferences?: { email: boolean; whatsapp: boolean };
    history?: Array<{ version: number; createdAt: string }>;
    /** What the parent asked the plan to focus on, so checkout does not ask twice. */
    planPrompt?: string;
  };
  content: ReportContent;
  entitlement?: {
    regenerationsRemaining: number;
    regenerationsUsed: number;
    versions: number;
    expiresAt: string;
    expired: boolean;
  };
}

export type OrderStatus = 'created' | 'pending' | 'paid' | 'failed' | 'cancelled' | 'refunded';

export interface Order {
  orderId: string;
  /** First report of the session, kept for old clients. */
  reportId: string;
  /** The assessment this order pays for. Absent on orders created before the session model. */
  assessmentId?: string;
  /** Every report of the session this order unlocks. Empty on pre-session orders — treat as `[reportId]`. */
  reportIds: string[];
  status: OrderStatus;
  amount: number;
  baseAmount: number;
  taxAmount: number;
  discountAmount: number;
  currency: string;
  gateway: 'mock' | 'razorpay' | 'stripe' | 'subscription';
  /** Set when a Nurture Plus subscription unlocked this session instead of a payment. */
  subscriptionId?: string;
  promoCode?: string;
  invoiceId?: string;
  failureReason?: string;
  createdAt: string;
}

export interface Transaction extends Order {
  reportRef?: string;
  childName?: string;
}

export interface Quote {
  currency: string;
  listAmount: number;
  baseAmount: number;
  discountAmount: number;
  taxAmount: number;
  totalAmount: number;
  taxLabel: string;
  taxInclusive: boolean;
  promoCode?: string;
}

export interface Checkout {
  gateway: 'mock' | 'razorpay';
  gatewayOrderId: string;
  amount: number;
  currency: string;
  paymentId?: string;
  methods?: string[];
  key?: string;
  name?: string;
  description?: string;
  prefill?: { name?: string; email?: string; contact?: string };
  /** Set on a Nurture Plus subscription's checkout — Razorpay's `checkout.js` opens with
   * `subscription_id` instead of `order_id` for this flow. */
  subscriptionId?: string;
}

// ── Nurture Plus subscription ───────────────────────────────────────────────

export type SubscriptionStatus =
  | 'created'
  | 'authenticated'
  | 'active'
  | 'pending'
  | 'halted'
  | 'cancelled'
  | 'completed'
  | 'expired';

export interface SubscriptionQuota {
  month: string;
  used: number;
  remaining: number;
  perMonth: number;
}

export interface Subscription {
  subscriptionId: string;
  plan: 'plus_quarterly';
  status: SubscriptionStatus;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  quota: SubscriptionQuota;
}

export interface AppConfig {
  questionBankVersion: string;
  /** The country the plan prices are for (the account's, or India for a visitor). */
  country?: string;
  /** Channels the API can deliver on right now, in order of preference. Absent on older APIs. */
  messagingChannels?: Channel[];
  /** Daily WhatsApp reminders are switched on for everyone. */
  whatsappRemindersEnabled?: boolean;
  /** "Start without an account" is offered; absent or false on older APIs. */
  guestCheckin?: boolean;
  contentVersion: string;
  /** `minMonths` (18, replacing the old 12-month/1-year floor) takes precedence over `minYears` when present. */
  ageRange: { minYears: number; minMonths?: number; maxYears: number };
  entitlement: { regenerationsPerPurchase: number; windowDays: number };
  pricing: Record<
    'IN' | 'US' | 'CA',
    { currency: string; listAmount: number; launchAmount: number; taxLabel: string; taxInclusive: boolean }
  >;
  plans: {
    /** `launchAmount` is the price to print: the GST-inclusive total, or the pre-tax price when prices exclude GST (then `taxAmount` and `totalAmount` say what is added and charged). */
    unlock: { currency: string; listAmount: number; launchAmount: number; taxLabel: string; taxInclusive: boolean; taxAmount?: number; totalAmount?: number };
    plus?: { currency: string; amount: number; period: string; unlocksPerMonth: number };
  };
  report: {
    pageTitles: readonly string[];
    freePages: number[];
    lockedPages: Array<{ page: number; title: string; description: string }>;
    disclaimer: string;
  };
}

// ---------- Home programme progress (GET /programme/:assessmentId) ----------
export type RoomKey = 'floor' | 'table' | 'meal' | 'calm' | 'move';
export type Outcome = 'enjoyed' | 'tried' | 'not_today';
export type StickerKind = 'star' | 'smile' | 'heart';

export interface ProgrammeActivity {
  exerciseId: string;
  title: string;
  room: RoomKey;
  roomLabel: string;
  durationMinutes: number;
  pillar: Pillar;
  index: number;
  phase?: 1 | 2;
}

export interface WeekSummary {
  weekStart: string;
  today: string;
  programmeWeek: number;
  days: Array<{ date: string; stickers: StickerKind[]; logs: Array<{ exerciseId: string; outcome: Outcome; sticker?: StickerKind }> }>;
  activeDays: number;
  perActivity: Record<string, number>;
  todaysPick?: string;
  activities: ProgrammeActivity[];
}

export interface GoalReached {
  assessmentId: string;
  exerciseId: string;
  /** What took its place; absent at the end of a skill chain. */
  nextExerciseId?: string;
  reachedAt: string;
}

/** Goals reached on top of the stored programme: activities that joined later and each goal's wording. */
export interface Progression {
  nextUp: Array<Extract<Block, { kind: 'exercise' }>>;
  goals: Record<string, string>;
  reached: GoalReached[];
  undoable: string[];
}

export interface ProgrammeResponse {
  assessmentId: string;
  childId: string;
  programme: ProgrammeDocument;
  week: WeekSummary;
  /** Older API builds omit it. */
  progression?: Progression;
}

// ── Moments ("log a moment") ──
export const MOMENT_TRIGGERS = ['noise', 'change', 'told_no', 'transition', 'hungry_tired', 'crowd', 'screen_off', 'unknown'] as const;
export const MOMENT_HELPERS = ['quiet_space', 'deep_pressure', 'distraction', 'waited', 'calm_voice', 'movement', 'nothing'] as const;
export const GOOD_AREAS = ['talking', 'playing', 'hands', 'calm', 'self_care', 'other'] as const;
export type MomentKind = 'hard' | 'good' | 'mood';
export type MomentTrigger = (typeof MOMENT_TRIGGERS)[number];
export type MomentHelper = (typeof MOMENT_HELPERS)[number];
export type GoodArea = (typeof GOOD_AREAS)[number];

export interface Moment {
  momentId: string;
  childId: string;
  kind: MomentKind;
  date: string;
  at: string;
  intensity?: 'small' | 'medium' | 'big';
  duration?: 'under5' | '5to15' | 'over15';
  triggers?: MomentTrigger[];
  helped?: MomentHelper[];
  area?: GoodArea;
  mood?: 1 | 2 | 3 | 4 | 5;
  before?: string;
  after?: string;
  note?: string;
}

export type MomentInput = Omit<Moment, 'momentId' | 'at' | 'date'> & { date?: string };

export interface MomentSummary {
  from: string;
  to: string;
  hard: number;
  bigHard: number;
  good: number;
  moodDays: number;
  topTriggers: Array<{ key: MomentTrigger; count: number }>;
  topHelpers: Array<{ key: MomentHelper; count: number }>;
}
